import { isAppLocale, type AppLocale } from './languages'

/** Public URL path segment (Ukrainian uses `/ua`, not `/uk`). */
export function localeUrlSegment(locale: AppLocale): string {
  return locale === 'uk' ? 'ua' : locale
}

/** Map a URL path segment to internal locale code. */
export function localeFromUrlSegment(segment: string): AppLocale | null {
  const lower = segment.toLowerCase()
  if (lower === 'ua') return 'uk'
  if (lower === 'uk') return null
  return isAppLocale(lower) ? lower : null
}

function localePrefixFromSegment(segment: string): AppLocale | null {
  const lower = segment.toLowerCase()
  if (lower === 'ua' || lower === 'uk') return 'uk'
  return isAppLocale(lower) ? lower : null
}

export function localeFromPathname(pathname: string): AppLocale | null {
  const segment = pathname.split('/').filter(Boolean)[0]
  if (!segment) return null
  return localeFromUrlSegment(segment)
}

export function stripLocalePrefix(pathname: string): string {
  const segment = pathname.split('/').filter(Boolean)[0]
  if (!segment || !localePrefixFromSegment(segment)) return pathname || '/'
  const rest = pathname.split('/').filter(Boolean).slice(1).join('/')
  return rest ? `/${rest}` : '/'
}

export function pathWithLocale(
  locale: AppLocale,
  pathname = '/',
  search = '',
  hash = '',
): string {
  const rest = stripLocalePrefix(pathname)
  const suffix = rest === '/' ? '' : rest
  return `/${localeUrlSegment(locale)}${suffix}${search}${hash}`
}

export function localeRootPath(locale: AppLocale, search = '', hash = ''): string {
  return `/${localeUrlSegment(locale)}${search}${hash}`
}

export function readBrowserUrl(): { pathname: string; search: string; hash: string } {
  if (typeof window === 'undefined') {
    return { pathname: '/', search: '', hash: '' }
  }
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  }
}

export function currentPathWithLocale(locale: AppLocale): string {
  const { pathname, search, hash } = readBrowserUrl()
  return pathWithLocale(locale, pathname, search, hash)
}

export function replaceLocaleInUrl(locale: AppLocale): void {
  if (typeof window === 'undefined') return
  const next = currentPathWithLocale(locale)
  const current = window.location.pathname + window.location.search + window.location.hash
  if (current !== next) {
    window.history.replaceState(window.history.state, '', next)
  }
}

export function ensureLocalePrefix(locale: AppLocale): void {
  if (typeof window === 'undefined') return
  if (localeFromPathname(window.location.pathname) === locale) return
  const { pathname, search, hash } = readBrowserUrl()
  window.history.replaceState(window.history.state, '', pathWithLocale(locale, pathname, search, hash))
}

/** Redirect legacy `/uk/...` bookmarks to `/ua/...`. */
export function redirectLegacyUkLocalePath(): void {
  if (typeof window === 'undefined') return
  const { pathname, search, hash } = readBrowserUrl()
  const parts = pathname.split('/').filter(Boolean)
  if (parts[0]?.toLowerCase() !== 'uk') return
  parts[0] = 'ua'
  const next = `/${parts.join('/')}${search}${hash}`
  window.history.replaceState(window.history.state, '', next)
}
